import React from 'react';
import { accent } from '../helpers';
import { ForgeChateau } from './Chateau';

/**
 * Template "statement" — une phrase seule, plein écran.
 *
 * Sert aux moments où le conducteur demande un écran sans matière :
 * la phrase unique, les phrases de bascule, les consignes d'interaction,
 * et la slide finale conçue pour être photographiée.
 *
 * ⚠️ Tailles bornées par la HAUTEUR autant que par la largeur
 * (`min(vw, vh)`), et réduites quand la phrase est longue. La page de
 * conférence met déjà le `font-size` racine à l'échelle de la largeur :
 * une taille fixe déborderait dans le bandeau de chapitre sur un écran
 * large et court, ou rognerait la note en bas.
 *
 * Champs de slide :
 *  - kicker    : petit label au-dessus (ex. "Interaction 2")
 *  - title     : la phrase (obligatoire)
 *  - note      : ligne discrète en dessous
 *  - accent    : "cyan" (défaut) | "red" | "amber" | "violet"
 *      Colore le titre ET le kicker. Sur la slide qui OUVRE un cas de
 *      « Preuve terrain », il doit valoir le `ton` du cas — c'est le seul
 *      endroit du deck où la couleur du titre porte une information.
 *  - contact   : { name, linkedin } — affiché en bas, discret
 *  - qr        : url encodée dans un QR minuscule en coin
 *  - intro     : "chateau" — la slide 27 : château de cartes, chiffres et
 *      note, révélés appui par appui (`etape`). Voir Chateau.jsx.
 */
const StatementSlide = ({ slide, slideKey, etape = 0 }) => {
    if (slide.intro === 'chateau') {
        return <ForgeChateau slide={slide} slideKey={slideKey} etape={etape} />;
    }

    /* Palette sortie du fichier : StandardSlide en a besoin des mêmes, et deux
       tables divergentes finiraient par donner deux ambres différents. */
    const ton = accent(slide.accent);

    // Une phrase de 200 signes ne peut pas tenir à la taille d'une phrase de 40.
    const len = (slide.title || '').length;
    const titleSize = len > 150 ? 'clamp(1.4rem, min(3vw, 6vh), 3rem)'
        : len > 90 ? 'clamp(1.7rem, min(3.8vw, 7.5vh), 3.75rem)'
            : 'clamp(2rem, min(5vw, 10vh), 4.75rem)';

    return (
        <div key={slideKey} className="h-full flex flex-col items-center justify-center px-16 pb-16 text-center relative overflow-hidden">
            <div className="relative z-10 max-w-6xl flex flex-col items-center gap-[3vh]">
                {slide.kicker && (
                    <p
                        className={`font-mono uppercase tracking-[0.3em] opacity-70 animate-fade-in-up ${ton.texte}`}
                        style={{ fontSize: 'clamp(0.7rem, min(1.3vw, 2.4vh), 1.5rem)' }}
                    >
                        {slide.kicker}
                    </p>
                )}

                <h1
                    className={`font-black leading-[1.15] bg-gradient-to-r ${ton.titre} bg-clip-text text-transparent animate-fade-in-up stagger-1`}
                    style={{ fontSize: titleSize }}
                >
                    {slide.title}
                </h1>

                {slide.note && (
                    <p
                        className="text-slate-300/70 font-light leading-snug animate-fade-in-up stagger-3"
                        style={{ fontSize: 'clamp(0.9rem, min(1.9vw, 3.4vh), 1.9rem)' }}
                    >
                        {slide.note}
                    </p>
                )}
            </div>

            {/* Remontés au-dessus des repères du diaporama : la barre de progression
                (en bas à gauche) et les boutons de navigation (en bas à droite)
                sont posés à `bottom-8` par le conteneur parent. Sur la slide de
                clôture — celle qui reste affichée pendant la pause et qui se fait
                photographier — le QR passait sous les boutons et le contact sous
                les points de progression. */}
            {slide.contact && (
                <div className="absolute bottom-[13vh] left-16 text-left animate-fade-in-up stagger-4">
                    <p
                        className="font-bold text-white/90"
                        style={{ fontSize: 'clamp(0.9rem, min(1.8vw, 3.2vh), 1.9rem)' }}
                    >
                        {slide.contact.name}
                    </p>
                    {slide.contact.linkedin && (
                        <p
                            className="text-blue-300/70 font-mono"
                            style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.25rem)' }}
                        >
                            {slide.contact.linkedin}
                        </p>
                    )}
                </div>
            )}

            {slide.qr && (
                <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(slide.qr)}`}
                    alt="QR contact"
                    className="absolute bottom-[13vh] right-16 rounded-lg bg-white p-2 opacity-80 animate-fade-in-scale stagger-4"
                    style={{ width: 'clamp(3.5rem, 9vh, 6rem)', height: 'clamp(3.5rem, 9vh, 6rem)' }}
                />
            )}
        </div>
    );
};

export default StatementSlide;
